import { i18n } from '@/i18n';
import { CampaignProgram } from '@/models/authModels';
import { Campaign, CampaignStats } from '@/models/campaignModels';
import { AmbassadorError, DispatchEmptyObject, DispatchObject } from '@/models/globalModels';
import { StatsMetric } from '@/models/statsModels';
import ApiService from '@/services/ApiService';
import LocalDataService from '@/services/LocalDataService';

import * as t from '../types';

// Loads campaign data after ambassador profile URLs are fetch
export const loadAmbassadorCampaigns = (campaigns: (Campaign | CampaignProgram)[]) => (dispatch: any) => {
  // only loads campaigns that are enabled to show dashboard content
  const _campaigns = campaigns.filter((campaign) => !campaign.is_hidden_dashboard);
  if (_campaigns?.length > 0) {
    dispatch(doLoadCampaignsFromShareLinks(_campaigns));
    const savedCampaignId = LocalDataService.getCampaignId();
    if (savedCampaignId) {
      const campaign = _campaigns.find((c) => c.campaign_id === savedCampaignId);
      if (campaign)
        dispatch(doSelectCampaign(campaign)); //TODO: Check if portal_campaign is set from customization ID
      else dispatch(doSelectCampaign(_campaigns[0])); //TODO: Check if portal_campaign is set from customization ID
    } else dispatch(doSelectCampaign(_campaigns[0])); //TODO: Check if portal_campaign is set from customization ID
  }
  if (_campaigns?.length > 1) dispatch(doSetMultipleCampaigns());
  if (_campaigns?.length == 0) dispatch(doSetNoCampaignStatus(i18n.t('dashboard:messages.noCampaignsMsg')));
};

// fetches ambassador stats for the selected campaign
export const doGetCampaignStats = (campaign_uid: number, uid: number, start_date: string, end_date: string) => (dispatch: any) => {
  dispatch(doStatsFetching());
  const auth = LocalDataService.getAuth();
  ApiService.setToken(auth);
  ApiService.getAmbassadorStatsByCampaign(campaign_uid, uid, start_date, end_date)
    .then((campaignStats) => {
      const campaignUid = campaignStats.campaign_uid;
      LocalDataService.saveCampaignId(campaignUid);
      dispatch(doStatsFetch(campaignStats));
    })
    .catch((e) => {
      dispatch(doStatsFetchError(e));
    });
};

// get ambassador stats with campaign optional
export const doGetAmbassadorStats = (uid: number, campaign_uid?: number) => (dispatch: any) => {
  try {
    dispatch(doAmbassadorStatsFetching());
    const auth = LocalDataService.getAuth();
    ApiService.setToken(auth);
    ApiService.getAmbassadorStats(uid, campaign_uid)
      .then((campaignStats: any) => {
        dispatch(doAmbassadorStatsFetch(campaignStats));
      })
      .catch((e) => {
        dispatch(doAmbassadorStatsFetchError(e));
      });
  } catch (e) {
    console.log('doGetAmbassadorStats ===>', e);
  }
};

export const doGetStatsMetrics =
  (campaign_uid: number, uid: number, start_date: string, end_date: string, interval: string) => (dispatch: any) => {
    dispatch(doGetStatsMetricsFetching());
    const auth = LocalDataService.getAuth();
    ApiService.setToken(auth);
    // special format for stats metrics YYYY-MM-DD
    // const startDateFormatted = moment(start_date, 'MM/DD/YYYY').format("YYYY-MM-DD");
    // const endDateFormatted = moment(end_date, 'MM/DD/YYYY').format("YYYY-MM-DD");
    ApiService.getStatsMetrics(campaign_uid, uid, start_date, end_date, interval)
      .then((metrics) => {
        dispatch(doGetStatsMetricsFetch(metrics));
      })
      .catch((e) => {
        dispatch(doGetStatsMetricsFetchError(e));
      });
  };

export const doTrackShareClick = (shortCode: string, clickType: string) => (dispatch: any) => {
  const auth = LocalDataService.getAuth();
  ApiService.setToken(auth);
  // no need to validate response at the moment
  const payload = {
    short_code: shortCode,
    social_name: clickType,
    ...(clickType === 'copy' ? { recipient_email: 'clipboard@example.com' } : {}),
    ...(clickType === 'copy' ? { recipient_username: 'clipboard' } : {}),
  };
  ApiService.trackShareClick(payload).catch((e) => {
    console.error('Error tracking share: ', e);
  });
};

// REDUX updates //
const doLoadCampaignsFromShareLinks =
  (campaigns: (Campaign | CampaignProgram)[]) => (dispatch: DispatchObject<(Campaign | CampaignProgram)[]>) => {
    dispatch({
      type: t.SET_CAMPAIGNS,
      payload: campaigns,
    });
  };

export const doSelectCampaign = (campaign: Campaign | CampaignProgram) => (dispatch: any) => {
  LocalDataService.saveCampaignId(campaign.campaign_id);
  dispatch({
    type: t.SET_SELECTED_CAMPAIGN,
    payload: campaign,
  });
  // checks if campaign inactive to trigger status
  if (!campaign.is_active) dispatch(doSetInactiveCampaignStatus(i18n.t('dashboard:messages.inactiveCampaignMsg')));
};

const doSetMultipleCampaigns = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.SET_MULTIPLE_CAMPAIGNS,
  });
};

const doSetNoCampaignStatus = (message: string) => (dispatch: DispatchObject<string>) => {
  dispatch({
    type: t.SET_NO_CAMPAIGNS,
    payload: message,
  });
};

const doSetInactiveCampaignStatus = (message: string) => (dispatch: DispatchObject<string>) => {
  dispatch({
    type: t.SET_INACTIVE_CAMPAIGN,
    payload: message,
  });
};

const doStatsFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.CAMPAIGN_STATS_FETCHING,
  });
};

const doStatsFetch = (stats: CampaignStats) => (dispatch: any) => {
  dispatch({
    type: t.CAMPAIGN_STATS_FETCH,
    payload: { ...stats },
  });
};

const doStatsFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.CAMPAIGN_STATS_FETCH_ERROR,
    payload: { ...error },
  });
};

const doAmbassadorStatsFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.AMBASSADOR_STATS_FETCHING,
  });
};

const doAmbassadorStatsFetch = (stats: CampaignStats) => (dispatch: any) => {
  dispatch({
    type: t.AMBASSADOR_STATS_FETCH,
    payload: { ...stats },
  });
};

const doAmbassadorStatsFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.AMBASSADOR_STATS_FETCH_ERROR,
    payload: { ...error },
  });
};

const doGetStatsMetricsFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.STATS_METRICS_FETCHING,
  });
};

const doGetStatsMetricsFetch = (metrics: StatsMetric[]) => (dispatch: any) => {
  dispatch({
    type: t.STATS_METRICS_FETCH,
    payload: { ...metrics },
  });
};

const doGetStatsMetricsFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.STATS_METRICS_FETCH_ERROR,
    payload: { ...error },
  });
};
